import { createElement } from 'react';
import { House } from 'lucide-react';
import { useLocation, useMatches, useParams } from 'react-router-dom';

import {
  useCurrentTenantID,
  useFindConnector,
  useFindProvider,
} from '@/hooks/scoped';
import type { RouteHandle } from '@/navigation/route-handle';
import { buildBreadcrumbHref } from '@/utils/breadcrumb-utils';
import { shortUUID, type UUID } from '@/utils/uuid';

type BreadcrumbItem = {
  label: string;
  icon?: React.ReactNode;
  to?: string;
};

/**
 * Determines the "view mode" for a given section (connectors, providers, etc.)
 * This is used to build the correct navigation URLs.
 */
function pathModeForSection(section: string): string {
  const modes: Record<string, string> = {
    connectors: 'view',
    providers: 'settings',
  };
  return modes[section] ?? 'configure';
}

/**
 * Formats a URL slug into a human-readable label.
 * Converts kebab-case/snake_case to Title Case.
 */
function formatEntitySlug(slug: string): string {
  return slug
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, (match) => match.toUpperCase());
}

function connectorTabLabel(tabId: string): string {
  const labels: Record<string, string> = {
    details: 'Details',
    features: 'Features',
    catalog: 'Catalog',
  };

  return labels[tabId] ?? formatEntitySlug(tabId);
}

function secretsTabLabel(tabId: string): string {
  const labels: Record<string, string> = {
    'secrets-list': 'Secrets',
    'secret-templates': 'Templates',
  };

  return labels[tabId] ?? formatEntitySlug(tabId);
}

/**
 * Builds a breadcrumb item with proper URL handling.
 * Automatically preserves query parameters (e.g., query-based tab state) and
 * path-based tabs (e.g., :tabId? route params) when building links.
 */
function createBreadcrumbItem(
  label: string,
  basePath: string | undefined,
  location: ReturnType<typeof useLocation>,
  preserveQuery: boolean = true
): BreadcrumbItem {
  return {
    label,
    to: basePath
      ? buildBreadcrumbHref(basePath, preserveQuery, location)
      : undefined,
  };
}

export function useHideBreadcrumbs(): boolean {
  const matches = useMatches();
  return matches.some(
    (m) => (m.handle as RouteHandle | undefined)?.hideBreadcrumbs === true
  );
}

export function useFullWidth(): boolean {
  const matches = useMatches();
  return matches.some(
    (m) => (m.handle as RouteHandle | undefined)?.fullWidth === true
  );
}

export function useFlexHeight(): boolean {
  const matches = useMatches();
  return matches.some(
    (m) => (m.handle as RouteHandle | undefined)?.flexHeight === true
  );
}

/**
 * Routes flagged with `deferLoading` opt out of the shared layout's blocking
 * spinner during the initial tenant fetch, so the page can render its own
 * skeleton immediately instead.
 */
export function useDeferLoading(): boolean {
  const matches = useMatches();
  return matches.some(
    (m) => (m.handle as RouteHandle | undefined)?.deferLoading === true
  );
}

export function useBreadcrumbs() {
  const { slug, connectorId, providerId } = useParams() as {
    slug?: string;
    connectorId?: string;
    providerId?: string;
  };
  const location = useLocation();
  const matches = useMatches();
  const tenantId = useCurrentTenantID();

  const { data: connector } = useFindConnector(
    tenantId,
    connectorId as UUID | undefined
  );

  const { data: provider } = useFindProvider(
    tenantId,
    providerId as UUID | undefined
  );

  const breadcrumbItems: BreadcrumbItem[] = [];
  const pathSegments = location.pathname.split('/').filter(Boolean);
  const manageIndex = pathSegments.indexOf('manage');

  // Only build breadcrumbs for manage section pages
  if (manageIndex === -1 || !pathSegments[manageIndex + 1]) {
    return breadcrumbItems;
  }

  const section = pathSegments[manageIndex + 1];
  const sectionLabel = formatEntitySlug(section);
  const remainingSegments = pathSegments.slice(manageIndex + 2);

  // Always start manage breadcrumbs with Home.
  breadcrumbItems.push({
    ...createBreadcrumbItem('Home', `/${slug}`, location, true),
    icon: createElement(House, { className: 'size-4' }),
  });

  // Show breadcrumbs only if we're drilling into specific items (deeper than list page)
  if (remainingSegments.length === 0) {
    // At the list page level only
    breadcrumbItems.push({
      label: sectionLabel,
      to: undefined, // Current page, no link
    });
    return breadcrumbItems;
  }

  // Add section link (list page)
  breadcrumbItems.push(
    createBreadcrumbItem(
      sectionLabel,
      `/${slug}/manage/${section}`,
      location,
      true
    )
  );

  // Check if we're navigating to a catalog entity (has 'entities' and connectorId)
  const entitiesIndex = remainingSegments.indexOf('entities');
  if (entitiesIndex !== -1 && entitiesIndex > 0) {
    const urlConnectorId = remainingSegments[entitiesIndex - 1];

    // Add connector breadcrumb with connector name
    breadcrumbItems.push(
      createBreadcrumbItem(
        connector?.name ?? `Connector ${shortUUID(urlConnectorId)}...`,
        `/${slug}/manage/${section}/${urlConnectorId}/${pathModeForSection(section)}`,
        location,
        true
      )
    );

    // Keep Catalog as an intermediate crumb for connector entity drill-in pages.
    if (section === 'connectors') {
      breadcrumbItems.push(
        createBreadcrumbItem(
          'Catalog',
          `/${slug}/manage/${section}/${urlConnectorId}/${pathModeForSection(section)}/catalog`,
          location,
          true
        )
      );
    }

    // Add entity breadcrumb (current page, no link)
    const entitySlug = remainingSegments[entitiesIndex + 1];
    if (entitySlug) {
      const entityLabel = formatEntitySlug(entitySlug);
      breadcrumbItems.push({ label: entityLabel });
    }

    return breadcrumbItems;
  }

  // Handle connector or provider detail pages
  const firstSegment = remainingSegments[0];
  const subSection = remainingSegments[1];
  const pathMode = pathModeForSection(section);
  const isOnEntityPage = subSection === pathMode;
  const hasTabId = remainingSegments.length > 2; // ID, mode, and potentially tabId

  // Add connector/provider link with tab preservation
  // Show the link when: on entity page (with/without tabs), or drilling into sub-resources
  if (firstSegment === connectorId && remainingSegments.length > 1) {
    breadcrumbItems.push(
      createBreadcrumbItem(
        connector?.name ?? `Connector ${shortUUID(firstSegment)}...`,
        `/${slug}/manage/${section}/${firstSegment}/${pathMode}`,
        location,
        true // Preserve tabs in query params
      )
    );
  } else if (firstSegment === providerId && remainingSegments.length > 1) {
    breadcrumbItems.push(
      createBreadcrumbItem(
        provider?.name ?? `Provider ${shortUUID(firstSegment)}...`,
        `/${slug}/manage/${section}/${firstSegment}/${pathMode}`,
        location,
        true // Preserve tabs in query params
      )
    );
  }

  // Keep Templates as an intermediate crumb for secret template drill-in pages.
  if (
    section === 'secrets' &&
    firstSegment === 'templates' &&
    remainingSegments.length > 1
  ) {
    breadcrumbItems.push(
      createBreadcrumbItem(
        'Templates',
        `/${slug}/manage/${section}/secret-templates`,
        location,
        true
      )
    );
  }

  // Add the current page label (only if on entity page with tabs)
  if (isOnEntityPage && hasTabId) {
    const matchedBreadcrumbLabel = [...matches]
      .reverse()
      .map((match) => (match.handle as RouteHandle | undefined)?.breadcrumb)
      .find((label) => typeof label === 'string' && label.length > 0);

    const lastSegment = remainingSegments[remainingSegments.length - 1];

    // Skip 'Features' for connectors — it is the default view so it duplicates the connector breadcrumb.
    if (section === 'connectors' && lastSegment === 'features') {
      // no-op: connector name is already the terminal breadcrumb
    } else {
      const currentPageLabel =
        section === 'connectors'
          ? connectorTabLabel(lastSegment)
          : (matchedBreadcrumbLabel ?? formatEntitySlug(lastSegment));

      breadcrumbItems.push({ label: currentPageLabel });
    }
  } else if (!isOnEntityPage) {
    // Show label for sub-resources (e.g., drilling into features, entities)
    const matchedBreadcrumbLabel = [...matches]
      .reverse()
      .map((match) => (match.handle as RouteHandle | undefined)?.breadcrumb)
      .find((label) => typeof label === 'string' && label.length > 0);

    const lastSegment = remainingSegments[remainingSegments.length - 1];
    const currentPageLabel =
      section === 'secrets' && remainingSegments.length === 1
        ? secretsTabLabel(lastSegment)
        : (matchedBreadcrumbLabel ?? formatEntitySlug(lastSegment));

    breadcrumbItems.push({ label: currentPageLabel });
  }

  return breadcrumbItems;
}
